import { createSelector } from '@reduxjs/toolkit';
import React from 'react';
import { useChannelStateContext } from 'stream-chat-react';
import { makeStyles } from 'tss-react/mui';

import { PortalConfigContext } from 'src/context';
import { NoMessagesEmptyState } from 'src/features/channel/NoMessagesEmptyState';
import { useCompanyChannel } from 'src/hooks/useCompanyChannel';
import { useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import { arrayToMap } from 'src/utils/array';
import { getProfileFromUser } from 'src/utils/UserUtils';

import { ChannelPlaceholderMessage } from './ChannelPlaceholderMessage';
import {
  getClientInIndividualChannel,
  getPotentialWelcomeChannel,
} from './chatFunctions';
import { useChatClient } from './useChatClient';

const useStyles = makeStyles()((theme) => ({
  placeholderMessageWrapper: {
    height: '100%',
    paddingTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(4.5),
    },
    [theme.breakpoints.down('xs')]: {
      paddingRight: theme.spacing(2),
    },
  },
}));

const selector = createSelector(
  [
    (state: RootState) => state.user.isClient,
    (state: RootState) => state.settings.messageSettings,
    (state: RootState) => state.clients.activeClients,
    (state: RootState) => state.users.internalUsers,
    (state: RootState) => state.clients.companies,
  ],
  (isClient, messagesSettings, activeClients, internalUsers, companies) => ({
    isClient,
    messagesSettings,
    activeClients: arrayToMap(activeClients, 'id'),
    internalUsers: arrayToMap(internalUsers, 'id'),
    companies: arrayToMap(companies, 'id'),
  }),
);

export const MessagesListEmptyState = () => {
  const [isPotentialWelcomeChannel, setIsPotentialWelcomeChannel] =
    React.useState<boolean>(false);
  const { classes } = useStyles();
  const chatClient = useChatClient();
  const isDefaultChannelCompany = useCompanyChannel();
  const { channel } = useChannelStateContext();
  const portalConfig = React.useContext(PortalConfigContext);

  const {
    isClient,
    messagesSettings,
    activeClients,
    internalUsers,
    companies,
  } = useAppSelector(selector);

  const clientCompany = React.useMemo(
    () => companies.get((channel.data?.companyID as string) || ''),
    [companies, channel.data?.companyID],
  );

  React.useEffect(() => {
    if (chatClient) {
      getPotentialWelcomeChannel({
        activeClients,
        companies,
        chatClient,
        clientCompany,
        isDefaultChannelCompany,
        channel,
        setIsPotentialWelcomeChannel,
      });
    }
  }, [
    activeClients,
    clientCompany,
    chatClient,
    isDefaultChannelCompany,
    channel,
  ]);

  const welcomeMessageSender = React.useMemo(() => {
    if (!channel || !channel?.state) return;
    if (channel?.data && isPotentialWelcomeChannel) {
      if (channel.data?.membershipType === 'company') {
        if (clientCompany) {
          const channelClient = getClientInIndividualChannel({
            activeClients,
            channel,
          });
          // `invitedBy` field exist in user model only. In order to render placeholder welcome message for company channel,
          // we need to iterate over all the members of the channel and find the member with invitation.
          // if not found, we will use the configured welcome message sender in message settings.
          const channelInviter = channelClient?.fields?.invitedBy;
          const messageSender = channelInviter
            ? internalUsers.get(channelInviter)
            : internalUsers.get(messagesSettings?.fields.senderUserId || '');

          return messageSender;
        }
      }
      // else, it is an individual channel because we do not set placeholder messages for group channels
      const channelClient = activeClients.get(
        channel.data?.singleClientId as string,
      );
      if (!channelClient) return;
      const inviter = internalUsers.get(channelClient.fields.invitedBy || '');
      const messageSender =
        inviter ??
        internalUsers.get(messagesSettings?.fields.senderUserId || '');
      return messageSender;
    }
    return undefined;
  }, [
    messagesSettings,
    internalUsers,
    activeClients,
    channel,
    isPotentialWelcomeChannel,
    clientCompany,
  ]);

  const showStandardPage = React.useMemo(() => {
    if (isClient) return true;
    if (messagesSettings?.fields?.isWelcomeMessageDisabled) return true;
    return !isPotentialWelcomeChannel;
  }, [
    isPotentialWelcomeChannel,
    messagesSettings?.fields?.isWelcomeMessageDisabled,
    isClient,
  ]);

  // For client users, irrespective of any message settings configurations, we will always show the StandardPage if there are no messages
  // For internal users, standard page will be rendered if welcome messages are disabled or if the channel is not a potential welcome channel
  if (showStandardPage) {
    return (
      <div className={classes.placeholderMessageWrapper}>
        <NoMessagesEmptyState />
      </div>
    );
  }

  // Welcome messages are enabled and it's definitely a welcome channel
  return (
    <div className={classes.placeholderMessageWrapper}>
      <ChannelPlaceholderMessage
        messageAuthor={
          welcomeMessageSender
            ? getProfileFromUser(welcomeMessageSender)
            : undefined
        }
        messageBody={
          messagesSettings?.fields.messageBody ||
          `Welcome to the ${portalConfig.name} portal.`
        }
      />
    </div>
  );
};
